import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import type { Tone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Affixed, Field, Input, Select, Textarea } from "@/components/ui/Field";
import { gmailCompose, whatsAppLink } from "@/features/communications/queries";
import { useSettings } from "@/features/settings/useSettings";
import {
  STUDIO_SIGNATORY,
  agreementMessage,
  signedCopyMessage,
  buildAgreement,
  newAgreementToken,
  releaseMessage,
  withdrawalAcknowledgement,
  type BookingState,
} from "@/lib/crew";
import {
  formatAgo,
  formatDateShort,
  type IsoDate,
  type IsoTime,
} from "@/lib/dates";
import { formatMoney, parseMoney } from "@/lib/format";
import { supabase } from "@/lib/supabase";
import { useUpdateSeat, type JobSeat, type TeamMember } from "./queries";

export const BOOKING_BADGE: Record<
  BookingState,
  { label: string; tone: Tone } | null
> = {
  empty: { label: "To be decided", tone: "amber" },
  self: null,
  draft: { label: "Agreement not sent", tone: "amber" },
  sent: { label: "Waiting for signature", tone: "amber" },
  signed: { label: "Signed", tone: "sage" },
  released: { label: "Released", tone: "neutral" },
};

/** A signed agreement still waits for the studio's signature (29 Oct 2026). */
export function seatBadge(
  seat: JobSeat,
  state: BookingState,
): { label: string; tone: Tone } | null {
  if (state === "signed")
    return seat.countersigned_at
      ? { label: "Signed by both", tone: "sage" }
      : { label: "Your signature next", tone: "amber" };
  return BOOKING_BADGE[state];
}

/** A five-minute download link to the agreement with both signatures. */
export async function signedAgreementUrl(token: string): Promise<string> {
  const { data, error } = await supabase.functions.invoke<{ url?: string }>(
    "crew-agreement",
    { body: { token, pdf: true } },
  );
  if (error || !data?.url)
    throw new Error("The signed copy could not be made. Please try again.");
  return data.url;
}

export function bookingState(
  seat: JobSeat,
  member: TeamMember | undefined,
): BookingState {
  if (seat.status === "released") return "released";
  if (!member) return "empty";
  if (!member.needs_agreement) return "self";
  return seat.status;
}

export function crewLink(token: string): string {
  return `${window.location.origin}/crew/${token}`;
}

const firstName = (member: TeamMember) =>
  (member.legal_name ?? member.name).split(" ")[0] ?? member.name;

/**
 * One person's booking on a job: what they're paid, what they must capture,
 * the agreement they sign, and how it ends (docs/principles.md 1.1-1.3).
 * Nothing is sent from here: the buttons open Gmail or WhatsApp with the
 * message filled in, and Jena presses Send.
 */
export function CrewBooking({
  jobId,
  jobTitle,
  eventDate,
  venue,
  seat,
  member,
}: {
  jobId: string;
  jobTitle: string;
  eventDate: IsoDate;
  venue: string | null;
  seat: JobSeat;
  member: TeamMember;
}) {
  const settings = useSettings();
  const update = useUpdateSeat(jobId);
  const state = bookingState(seat, member);
  const locked = state === "signed" || state === "released";

  const [fee, setFee] = useState(seat.fee !== null ? String(seat.fee) : "");
  const [travel, setTravel] = useState(
    seat.travel_fee !== null ? String(seat.travel_fee) : "",
  );
  const [callTime, setCallTime] = useState(seat.call_time?.slice(0, 5) ?? "");
  const [deliverables, setDeliverables] = useState(
    seat.deliverables.join("\n"),
  );
  const [brief, setBrief] = useState(seat.brief ?? "");
  const [releasing, setReleasing] = useState(false);
  const [releasedBy, setReleasedBy] = useState<"us" | "them">("us");
  const [reason, setReason] = useState("");
  const [signatory, setSignatory] = useState(STUDIO_SIGNATORY);
  const [downloading, setDownloading] = useState(false);

  const fields = {
    fee: parseMoney(fee),
    travel_fee: parseMoney(travel),
    call_time: callTime || null,
    deliverables: deliverables
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean),
    brief: brief.trim() || null,
  };
  const dirty =
    fields.fee !== seat.fee ||
    fields.travel_fee !== seat.travel_fee ||
    fields.call_time !== (seat.call_time?.slice(0, 5) ?? null) ||
    fields.deliverables.join("\n") !== seat.deliverables.join("\n") ||
    fields.brief !== seat.brief;

  const missing = [
    !member.email && !member.phone
      ? "an email address or phone number (their page under Team)"
      : null,
    fields.fee === null ? "the fee" : null,
    fields.deliverables.length === 0 ? "what they capture and deliver" : null,
    !settings.data?.crew_terms ? "the crew terms (Settings › Crew terms)" : null,
  ].filter(Boolean);

  function agreementText(): string {
    return buildAgreement({
      businessName: settings.data?.business_name ?? "De Leeuw Photo & Film",
      jobName: jobTitle,
      eventDate,
      venue,
      callTime: (fields.call_time as IsoTime | null) ?? null,
      role: seat.role,
      personName: member.legal_name ?? member.name,
      personEmail: member.email,
      personPhone: member.phone,
      fee: fields.fee,
      travelFee: fields.travel_fee,
      deliverables: fields.deliverables,
      brief: fields.brief,
      terms: settings.data?.crew_terms ?? "",
    });
  }

  /** Freeze the text, mark it sent, and open the message for Jena to send. */
  async function send(via: "gmail" | "whatsapp" | "copy") {
    const token = seat.agreement_token ?? newAgreementToken();
    const message = agreementMessage(
      firstName(member),
      jobTitle,
      eventDate,
      crewLink(token),
    );
    // Open the tab first, while the click still counts, so it isn't blocked.
    const tab = via === "copy" ? null : window.open("", "_blank");
    try {
      await update.mutateAsync({
        id: seat.id,
        ...fields,
        agreement_text: agreementText(),
        agreement_token: token,
        status: "sent",
        sent_at: new Date().toISOString(),
      });
    } catch {
      tab?.close();
      return;
    }
    if (via === "copy") {
      await navigator.clipboard.writeText(message);
      toast.success("Message copied", {
        description: "Paste it to them. The booking now shows as sent.",
      });
    } else if (tab) {
      tab.location.href =
        via === "gmail"
          ? gmailCompose(
              member.email ?? "",
              `Your booking: ${jobTitle}, ${formatDateShort(eventDate)}`,
              message,
            )
          : (whatsAppLink(member.phone, message) ?? "");
    }
  }

  async function release() {
    try {
      await update.mutateAsync({
        id: seat.id,
        status: "released",
        released_at: new Date().toISOString(),
        released_by: releasedBy,
        released_reason: reason.trim() || null,
      });
    } catch {
      return;
    }
    setReleasing(false);
    const message =
      releasedBy === "us"
        ? releaseMessage(firstName(member), jobTitle, eventDate, reason)
        : withdrawalAcknowledgement(firstName(member), jobTitle, eventDate);
    await navigator.clipboard.writeText(message).catch(() => undefined);
    toast.success(releasedBy === "us" ? "Released" : "Withdrawal recorded", {
      description:
        "The written message is copied: send it to them by email or WhatsApp so it is on record.",
    });
  }

  async function countersign() {
    const name = signatory.trim().replace(/\s+/g, " ");
    if (
      !window.confirm(
        `Sign ${firstName(member)}'s agreement as the studio, as ${name}? Your signature can't be taken off afterwards.`,
      )
    )
      return;
    try {
      // The database stamps the time and who is signed in.
      await update.mutateAsync({
        id: seat.id,
        countersigned_name: name,
        countersigned_at: new Date().toISOString(),
      });
    } catch {
      return;
    }
    toast.success("Signed by both", {
      description: "Download the signed copy or send it to them below.",
    });
  }

  async function download() {
    if (!seat.agreement_token) return;
    // Open the tab first, while the click still counts, so it isn't blocked.
    const tab = window.open("", "_blank");
    setDownloading(true);
    try {
      const url = await signedAgreementUrl(seat.agreement_token);
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (error) {
      tab?.close();
      toast.error((error as Error).message);
    } finally {
      setDownloading(false);
    }
  }

  /** Opens the message with their booking page, where the signed copy downloads. */
  async function sendSignedCopy(via: "gmail" | "whatsapp" | "copy") {
    if (!seat.agreement_token) return;
    const message = signedCopyMessage(
      firstName(member),
      jobTitle,
      eventDate,
      crewLink(seat.agreement_token),
    );
    if (via === "copy") {
      await navigator.clipboard.writeText(message);
      toast.success("Message copied", { description: "Paste it to them." });
      return;
    }
    window.open(
      via === "gmail"
        ? gmailCompose(
            member.email ?? "",
            `Your signed agreement: ${jobTitle}, ${formatDateShort(eventDate)}`,
            message,
          )
        : (whatsAppLink(member.phone, message) ?? ""),
      "_blank",
      "noopener",
    );
  }

  return (
    <div className="flex flex-col gap-3 border-l border-l-[var(--color-rule-strong)] py-2 pl-3">
      {state === "signed" ? (
        <p className="text-[length:var(--text-2xs)] text-sage">
          Signed by {seat.signed_name}{" "}
          {seat.signed_at ? formatAgo(seat.signed_at) : ""}.{" "}
          {seat.countersigned_at
            ? `You signed ${formatAgo(seat.countersigned_at)}. `
            : ""}
          {seat.brief_confirmed_at
            ? `Game plan confirmed ${formatAgo(seat.brief_confirmed_at)}.`
            : "Game plan not confirmed yet."}
        </p>
      ) : null}

      {state === "signed" && !seat.countersigned_at ? (
        <form
          className="flex flex-col gap-2 rounded-md bg-paper p-3"
          onSubmit={(event) => {
            event.preventDefault();
            void countersign();
          }}
        >
          <p className="text-[length:var(--text-sm)]">
            {firstName(member)} has signed. Sign as the studio, then send them
            the copy with both signatures.
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <Field label="Your full name" className="min-w-[12rem] flex-1">
              <Input
                value={signatory}
                autoComplete="name"
                onChange={(e) => setSignatory(e.target.value)}
              />
            </Field>
            <Button
              type="submit"
              size="sm"
              variant="primary"
              disabled={signatory.trim().length < 4 || !signatory.trim().includes(" ")}
              loading={update.isPending}
            >
              Sign as the studio
            </Button>
          </div>
          <p className="text-[length:var(--text-2xs)] text-taupe">
            Typing your name and signing here is your signature. The time is
            recorded with it.
          </p>
        </form>
      ) : null}

      {state === "signed" && seat.countersigned_at ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            loading={downloading}
            onClick={() => void download()}
          >
            Download signed PDF
          </Button>
          <span className="text-[length:var(--text-2xs)] text-taupe">
            Send them the signed copy:
          </span>
          {member.email ? (
            <Button size="sm" variant="ghost" onClick={() => void sendSignedCopy("gmail")}>
              Open in Gmail
            </Button>
          ) : null}
          {member.phone ? (
            <Button size="sm" variant="ghost" onClick={() => void sendSignedCopy("whatsapp")}>
              Open in WhatsApp
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => void sendSignedCopy("copy")}>
            Copy message
          </Button>
        </div>
      ) : null}
      {state === "sent" ? (
        <p className="text-[length:var(--text-2xs)] text-amber">
          Sent {seat.sent_at ? formatAgo(seat.sent_at) : ""}, not signed yet.
          Until it is signed, this person is not booked.
        </p>
      ) : null}
      {state === "released" ? (
        <p className="text-[length:var(--text-2xs)] text-taupe">
          {seat.released_by === "us" ? "Released by us" : "They withdrew"}{" "}
          {seat.released_at ? formatAgo(seat.released_at) : ""}
          {seat.released_reason ? `: ${seat.released_reason}` : "."}
        </p>
      ) : null}

      {locked ? (
        <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-[length:var(--text-sm)]">
          <dt className="text-taupe">Fee</dt>
          <dd>
            {seat.fee !== null ? formatMoney(seat.fee) : "—"}
            {seat.travel_fee ? ` + ${formatMoney(seat.travel_fee)} travel` : ""}
          </dd>
          <dt className="text-taupe">Must capture</dt>
          <dd>
            <ul>
              {seat.deliverables.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </dd>
        </dl>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Fee">
              <Affixed prefix="R">
                <Input
                  inputMode="decimal"
                  value={fee}
                  onChange={(e) => setFee(e.target.value)}
                />
              </Affixed>
            </Field>
            <Field label="Travel" hint="Leave empty if included.">
              <Affixed prefix="R">
                <Input
                  inputMode="decimal"
                  value={travel}
                  onChange={(e) => setTravel(e.target.value)}
                />
              </Affixed>
            </Field>
            <Field label="Call time">
              <Input
                type="time"
                value={callTime}
                onChange={(e) => setCallTime(e.target.value)}
              />
            </Field>
          </div>
          <Field
            label="What they capture and deliver"
            hint="One per line. These go into the agreement as must-haves."
          >
            <Textarea
              rows={Math.max(4, fields.deliverables.length + 1)}
              value={deliverables}
              onChange={(e) => setDeliverables(e.target.value)}
            />
          </Field>
          <Field
            label="Also note"
            hint="Anything else for this person: cards to collect, parking, dress."
          >
            <Textarea
              rows={2}
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
            />
          </Field>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={!dirty}
              loading={update.isPending}
              onClick={() => update.mutate({ id: seat.id, ...fields })}
            >
              Save booking
            </Button>
            {missing.length ? (
              <span className="text-[length:var(--text-2xs)] text-taupe">
                To send the agreement, add {missing.join(", ")}.
              </span>
            ) : (
              <>
                <span className="text-[length:var(--text-2xs)] text-taupe">
                  {state === "sent"
                    ? "Resend the agreement"
                    : "Send the agreement"}
                  :
                </span>
                {member.email ? (
                  <Button
                    size="sm"
                    variant={state === "draft" ? "primary" : "ghost"}
                    onClick={() => void send("gmail")}
                  >
                    Open in Gmail
                  </Button>
                ) : null}
                {member.phone ? (
                  <Button
                    size="sm"
                    variant={
                      state === "draft" && !member.email ? "primary" : "ghost"
                    }
                    onClick={() => void send("whatsapp")}
                  >
                    Open in WhatsApp
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void send("copy")}
                >
                  Copy message
                </Button>
              </>
            )}
          </div>
          {state === "sent" && dirty ? (
            <p className="text-[length:var(--text-2xs)] text-amber">
              You changed the booking after sending it: resend so they sign the
              new version.
            </p>
          ) : null}
        </>
      )}

      {(state === "sent" || state === "signed") && seat.agreement_token ? (
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={crewLink(seat.agreement_token)}
            target="_blank"
            rel="noreferrer noopener"
            className="text-[length:var(--text-2xs)] underline underline-offset-4"
          >
            Their booking page
          </a>
          {state === "signed" ? (
            <Link
              to={`/team/${member.id}`}
              className="text-[length:var(--text-2xs)] underline underline-offset-4"
            >
              {seat.paid_at
                ? `Paid ${formatDateShort(seat.paid_at.slice(0, 10))}`
                : "Record payment, invoice or rating"}
            </Link>
          ) : null}
          {!releasing ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setReleasing(true)}
            >
              End this booking
            </Button>
          ) : null}
        </div>
      ) : null}

      {releasing ? (
        <div className="flex flex-col gap-2 border-t pt-2">
          <p className="text-[length:var(--text-2xs)] text-taupe">
            A booking only ends in writing. This records it and copies the
            message to send them.
          </p>
          <div className="grid gap-2 sm:grid-cols-[12rem_minmax(0,1fr)]">
            <Field label="Who ended it">
              <Select
                value={releasedBy}
                onChange={(e) => setReleasedBy(e.target.value as "us" | "them")}
              >
                <option value="us">We release them</option>
                <option value="them">They withdrew</option>
              </Select>
            </Field>
            <Field label="Reason">
              <Input
                value={reason}
                placeholder={
                  releasedBy === "us"
                    ? "e.g. the couple changed to photo only"
                    : "what they said, and when"
                }
                onChange={(e) => setReason(e.target.value)}
              />
            </Field>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="primary"
              disabled={releasedBy === "them" && !reason.trim()}
              loading={update.isPending}
              onClick={() => void release()}
            >
              {releasedBy === "us" ? "Release" : "Record the withdrawal"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setReleasing(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
